export interface AppSettingsApiResponse {
  company_name: string;
  tax_id: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  mercantile_registry: string;
  currency_code: string;
  currency_locale: string;
  iva_rate_pct: string | number;
  order_email_overdue_enabled: boolean;
  order_email_overdue_days: number;
  order_call_overdue_enabled: boolean;
  order_call_overdue_days: number;
  order_confirm_overdue_enabled: boolean;
  order_confirm_overdue_days: number;
  subproject_pending_items_overdue_enabled: boolean;
  subproject_pending_items_overdue_days: number;
  subproject_cost_deviation_watch_enabled: boolean;
  subproject_cost_deviation_watch_pct: string | number;
  subproject_pending_orders_watch_enabled: boolean;
  subproject_pending_orders_watch_limit: number;
  updated_at: string;
}

export interface AppSettings {
  companyName: string;
  taxId: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  mercantileRegistry: string;
  currencyCode: string;
  currencyLocale: string;
  ivaRatePct: number;
  orderEmailOverdueEnabled: boolean;
  orderEmailOverdueDays: number;
  orderCallOverdueEnabled: boolean;
  orderCallOverdueDays: number;
  orderConfirmOverdueEnabled: boolean;
  orderConfirmOverdueDays: number;
  subprojectPendingItemsOverdueEnabled: boolean;
  subprojectPendingItemsOverdueDays: number;
  subprojectCostDeviationWatchEnabled: boolean;
  subprojectCostDeviationWatchPct: number;
  subprojectPendingOrdersWatchEnabled: boolean;
  subprojectPendingOrdersWatchLimit: number;
  updatedAt: string;
}

const toNumber = (value: string | number | null | undefined, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const DEFAULT_APP_SETTINGS: AppSettings = {
  companyName: 'Estudio Interior SL',
  taxId: 'B12345678',
  address: 'Calle del Diseño 25, Madrid',
  phone: '+34 91 123 4567',
  email: 'info@estudiointerior.com',
  website: 'www.estudiointerior.com',
  mercantileRegistry: 'Madrid, Tomo 123, Folio 456',
  currencyCode: 'EUR',
  currencyLocale: 'es-ES',
  ivaRatePct: 21,
  orderEmailOverdueEnabled: true,
  orderEmailOverdueDays: 3,
  orderCallOverdueEnabled: true,
  orderCallOverdueDays: 3,
  orderConfirmOverdueEnabled: true,
  orderConfirmOverdueDays: 3,
  subprojectPendingItemsOverdueEnabled: true,
  subprojectPendingItemsOverdueDays: 5,
  subprojectCostDeviationWatchEnabled: true,
  subprojectCostDeviationWatchPct: 10,
  subprojectPendingOrdersWatchEnabled: true,
  subprojectPendingOrdersWatchLimit: 3,
  updatedAt: '',
};

export const normalizeAppSettings = (
  settings: AppSettingsApiResponse | null | undefined,
): AppSettings => {
  if (!settings) return DEFAULT_APP_SETTINGS;

  return {
    companyName: settings.company_name,
    taxId: settings.tax_id,
    address: settings.address,
    phone: settings.phone,
    email: settings.email,
    website: settings.website,
    mercantileRegistry: settings.mercantile_registry,
    currencyCode: settings.currency_code || DEFAULT_APP_SETTINGS.currencyCode,
    currencyLocale: settings.currency_locale || DEFAULT_APP_SETTINGS.currencyLocale,
    ivaRatePct: toNumber(settings.iva_rate_pct, DEFAULT_APP_SETTINGS.ivaRatePct),
    orderEmailOverdueEnabled: settings.order_email_overdue_enabled,
    orderEmailOverdueDays: settings.order_email_overdue_days,
    orderCallOverdueEnabled: settings.order_call_overdue_enabled,
    orderCallOverdueDays: settings.order_call_overdue_days,
    orderConfirmOverdueEnabled: settings.order_confirm_overdue_enabled,
    orderConfirmOverdueDays: settings.order_confirm_overdue_days,
    subprojectPendingItemsOverdueEnabled: settings.subproject_pending_items_overdue_enabled,
    subprojectPendingItemsOverdueDays: settings.subproject_pending_items_overdue_days,
    subprojectCostDeviationWatchEnabled: settings.subproject_cost_deviation_watch_enabled,
    subprojectCostDeviationWatchPct: toNumber(
      settings.subproject_cost_deviation_watch_pct,
      DEFAULT_APP_SETTINGS.subprojectCostDeviationWatchPct,
    ),
    subprojectPendingOrdersWatchEnabled: settings.subproject_pending_orders_watch_enabled,
    subprojectPendingOrdersWatchLimit: settings.subproject_pending_orders_watch_limit,
    updatedAt: settings.updated_at,
  };
};
